import Api from "repository/Api"

/**
 * Get a map: its base details and its original, uploaded and modified sections, in one request (API 1.2)
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @returns {Promise<Object>} the payload as received
 */
export const getMap = async (projectId, name) => {
    const res = await Api.get(`/projects/${projectId}/maps/${encodeURIComponent(name)}`);
    return res.data;
};

/**
 * Save the values of a map's data (API 1.3). The whole map is sent every time: the API checks the shape and re-encodes all of it. The route does not
 * deserialise (API 0.1 as corrected), so the body is plain JSON and not the merge-patch type of the legacy writes.
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @param {number[][]} values - exactly rows by columns finite numbers: the matrix of the map's data axis
 * @returns {Promise<Object>} the single-map payload as received, with affectedMaps, affectedMapsState, undoBatchId and changedBytes added to it
 */
export const saveMap = async (projectId, name, values) => {
    const res = await Api.patch(`/projects/${projectId}/maps/${encodeURIComponent(name)}`, {values}, {headers: {"Content-Type": "application/json"}});
    return res.data;
};

/**
 * Save the values of one of a map's axes (API 1.3), on the route of the data save. Only the axis is sent, and the answer is the single-map payload plus
 * what a save adds to it. `affectedMaps` names every map that shares the axis's stored address (or just this one), and `affectedMapsState` says whether
 * each is modified now (R46).
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @param {'X'|'Y'} axis - the letter of the axis
 * @param {number[]} values - the flat list of exactly the size of the template row (R1)
 * @returns {Promise<Object>} the single-map payload as received, with affectedMaps, affectedMapsState, undoBatchId and changedBytes added to it
 */
export const saveAxis = async (projectId, name, axis, values) => {
    const res = await Api.patch(`/projects/${projectId}/maps/${encodeURIComponent(name)}`, {axis, values}, {headers: {"Content-Type": "application/json"}});
    return res.data;
};

//favourited

/**
 * Toggle the favourite star of a map (API 1.6, 4.6). The body is empty JSON; the route does not deserialise. The answer is the star's new state, which the
 * caller follows: a toggle is not idempotent, so what the star shows is what the API answers and never a local flip.
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @returns {Promise<boolean>} favourited - whether the map is a favourite after the toggle
 */
export const toggleFavourite = async (projectId, name) => {
    const res = await Api.patch(`/projects/${projectId}/maps/${encodeURIComponent(name)}/favourite`, {}, {headers: {"Content-Type": "application/json"}});
    return res.data.favourited;
};

//undo

/**
 * Get the undo list of a map (API 1.4, 4.7): the entries that can be reverted, newest first. The list merges the map's data and axis entries. An entry that
 * is not the newest on its map or axis, or is overlapped by a newer entry of another batch, comes with `undoable: false`; `batchSize` is the number of
 * entries (maps) of the entry's batch, more than one for a wizard's change (REQ E72).
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @returns {Promise<{id: string, batchId: string, axis: ?string, createdAt: string, undoable: boolean, batchSize: number}[]>}
 */
export const getUndoList = async (projectId, name) => {
    const res = await Api.get(`/projects/${projectId}/maps/${encodeURIComponent(name)}/undo`);
    return res.data["hydra:member"];
};

/**
 * Revert the map to before an undo entry (API 1.5, 4.8). The body is empty JSON; the route does not deserialise. The answer is the single-map payload plus
 * `affectedMaps`, `affectedMapsState` and `changedBytes` (it has no `undoBatchId`).
 * @param {string} projectId
 * @param {string} name - the map's name, which is its id
 * @param {string} entryId - the id of the entry, from getUndoList
 * @returns {Promise<Object>} the payload as received
 */
export const applyUndo = async (projectId, name, entryId) => {
    const res = await Api.post(`/projects/${projectId}/maps/${encodeURIComponent(name)}/undo/${encodeURIComponent(entryId)}`, {}, {headers: {"Content-Type": "application/json"}});
    return res.data;
};
